import { useState, useEffect } from 'react';
import { X, Cpu, Check } from 'lucide-react';
import { Button, Loader } from '@/components/ui';
import { sgmDefaultTemplate } from '@/data/templates';
import type { PhaseTemplate } from '@/types';

interface ComponentTemplate {
  id: string;
  oem: string;
  product: string;
  description: string;
  phases: PhaseTemplate[];
  created_at: string;
  updated_at: string;
  isDefault?: boolean;
}

interface ComponentTemplateSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: ComponentTemplate) => void;
}

const defaultComponentTemplate: ComponentTemplate = {
  id: 'default-sgm',
  oem: 'SGM',
  product: 'Exterior Lighting',
  description: 'SGM Exterior Lighting Default Template',
  phases: sgmDefaultTemplate,
  created_at: '',
  updated_at: '',
  isDefault: true
};

export function ComponentTemplateSelectModal({
  isOpen,
  onClose,
  onSelect
}: ComponentTemplateSelectModalProps) {
  const [templates, setTemplates] = useState<ComponentTemplate[]>([defaultComponentTemplate]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const getAccessToken = (): string | null => {
    const storedSession = localStorage.getItem('session');
    if (!storedSession) return null;
    try {
      const session = JSON.parse(storedSession);
      return session?.access_token || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      setSelectedTemplate('');
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = getAccessToken();
      const response = await fetch('/api/templates', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const userTemplates = (data.componentTemplates || []).map((t: ComponentTemplate) => ({
          ...t,
          isDefault: false
        }));
        setTemplates([defaultComponentTemplate, ...userTemplates]);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
  };

  const handleConfirm = () => {
    const template = templates.find(t => t.id === selectedTemplate);
    if (template) {
      onSelect(template);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            <Cpu className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="text-lg font-bold text-gray-800">Select Component Development Template</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader size="md" />
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleSelect(template.id)}
                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    selectedTemplate === template.id 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedTemplate === template.id 
                        ? 'border-green-500 bg-green-500' 
                        : 'border-gray-300'
                    }`}>
                      {selectedTemplate === template.id && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h4 className="font-semibold text-gray-800">
                          {template.oem} - {template.product}
                        </h4>
                        {template.isDefault && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-green-600 text-white rounded">Default</span>
                        )}
                      </div>
                      <div className="flex items-center mt-2 text-xs text-gray-400">
                        <span>{template.phases.length} phases</span>
                        <span className="mx-2">-</span>
                        <span>{template.phases.reduce((acc, phase) => acc + phase.tasks.length, 0)} tasks</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
          <Button variant="ghost" onClick={onClose} className="w-24">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!selectedTemplate}
            className="w-24"
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}

export type { ComponentTemplate };
